import { useCallback, useEffect, useState } from "react";

import { Auth } from "@aws-amplify/auth";

import { toastSuccess } from "@alpacahq/ui/old";

import {
  getCurrentAuthenticatedUser,
  UserNotAuthenticated,
} from "~/v2/api/auth/currentUser";
import { getErrorMessage } from "~/v2/api/hooks";

export const MFAMethods = {
  SOFTWARE_TOKEN_MFA: {
    method: "SOFTWARE_TOKEN_MFA",
    displayName: "time-based one-time password (TOTP)",
  },
  SMS_MFA: {
    method: "SMS_MFA",
    displayName: "text-message (SMS)",
  },
  NOMFA: {
    method: "NOMFA",
    displayName: "",
  },
} as const;

type MFAMethod = string | null;

// Methods accepted by `enable` - matches the Cognito challenge names
// that Auth.setPreferredMFA understands directly, so no translation is needed.
type MFAEnableMethod =
  | typeof MFAMethods.SMS_MFA.method
  | typeof MFAMethods.SOFTWARE_TOKEN_MFA.method;

interface UseMFAReturn {
  method: MFAMethod;
  isMFAEnabled: boolean;
  isMFALoading: boolean;
  TOTPQRCode: string | null;
  TOTPSetupVerificationError: string | null;
  refresh: () => Promise<void>;
  enable: (method?: MFAEnableMethod) => Promise<void>;
  disable: () => Promise<void>;
  setupTOTP: () => Promise<void>;
  verifyTOTPToken: (challengeAnswer: string) => Promise<void>;
}

/**
 * Wraps AWS Amplify MFA calls with local React state.
 * Drop-in replacement for the former Redux mfa reducer + thunks.
 */
export function useMFA(): UseMFAReturn {
  const [method, setMethod] = useState<MFAMethod>(null);
  const [TOTPQRCode, setTOTPQRCode] = useState<string | null>(null);
  const [TOTPSetupVerificationError, setTOTPSetupVerificationError] = useState<
    string | null
  >(null);

  const isMFALoading = method === null;
  const isMFAEnabled =
    method === MFAMethods.SMS_MFA.method ||
    method === MFAMethods.SOFTWARE_TOKEN_MFA.method;

  const refresh = useCallback(async () => {
    try {
      const user = await getCurrentAuthenticatedUser();

      await new Promise<void>((resolve) =>
        user.getUserData(() => resolve(), { bypassCache: true }),
      );

      const preferredMethod = await Auth.getPreferredMFA(user);
      setMethod(preferredMethod);
    } catch (err) {
      // Logged-out users have no Cognito session; skip MFA sync silently.
      // Any other failure (Cognito network/service error) is unexpected and
      // must surface so it isn't mistaken for a stuck loading state.
      if (!(err instanceof UserNotAuthenticated)) {
        throw err;
      }
    }
  }, []);

  const enable = useCallback(
    async (mfaMethod: MFAEnableMethod = MFAMethods.SMS_MFA.method) => {
      const user = await getCurrentAuthenticatedUser();
      await new Promise<void>((resolve) => {
        user.getUserData(
          () => {
            Auth.setPreferredMFA(user, mfaMethod)
              .then(() => refresh().then(resolve))
              .catch((err: unknown) => {
                console.error("error setting MFA method:", err);
                resolve();
              });
          },
          { bypassCache: true },
        );
      });
    },
    [refresh],
  );

  const disable = useCallback(async () => {
    const user = await getCurrentAuthenticatedUser();
    await new Promise<void>((resolve) => {
      user.getUserData(
        () => {
          Auth.setPreferredMFA(user, MFAMethods.NOMFA.method)
            .then(() => {
              toastSuccess("You have disabled MFA for your account.");
              return refresh().then(resolve);
            })
            .catch(() => resolve());
        },
        { bypassCache: true },
      );
    });
  }, [refresh]);

  const setupTOTPAction = useCallback(async () => {
    setTOTPSetupVerificationError(null);
    const user = await getCurrentAuthenticatedUser();
    const code = await Auth.setupTOTP(user);
    const authCode = `otpauth://totp/${user.attributes?.email || ""}?secret=${code}&issuer=Alpaca`;
    setTOTPQRCode(authCode);
  }, []);

  const verifyTOTPToken = useCallback(
    async (challengeAnswer: string) => {
      try {
        setTOTPSetupVerificationError(null);
        const user = await getCurrentAuthenticatedUser();
        await Auth.verifyTotpToken(user, challengeAnswer);
        setTOTPQRCode(null);
        await enable(MFAMethods.SOFTWARE_TOKEN_MFA.method);
      } catch (err) {
        console.error(err);
        setTOTPSetupVerificationError(
          err instanceof Error
            ? getErrorMessage(err)
            : "Verification failed. Please try again.",
        );
      }
    },
    [enable],
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    method,
    isMFAEnabled,
    isMFALoading,
    TOTPQRCode,
    TOTPSetupVerificationError,
    refresh,
    enable,
    disable,
    setupTOTP: setupTOTPAction,
    verifyTOTPToken,
  };
}
